<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form v-slot="{ values }"
              :initial-values="initial_values"
              :validation-schema="schema"
              class="mt-2 space-y-6"
              @submit="updateTask">
          <div class="absolute flex items-center gap-2 top-4 right-6">
            <ToggleStatusButton
              :class="[{ 'mt-10': inPlaybook }]"
              :prevent-request="true"
              :object="task_displayed"
              @status-changed="
                ($event) => {
                  status = $event
                }
              " />
            <div
              v-if="!inPlaybook && $route.name !== 'WorkspaceItem' && isModuleActivated(MODULES.WORKSPACE)"
              :class="[{ 'mt-3': task.workspaces_shared_in?.length }]">
              <div type="button" class="primary-outline-btn w-fit" @click="shareTask(task)">
                <Share2 class="w-6 h-6 mr-1 transition ease-in-out duration-side-menu-transition-duration" />
                {{ $t("Share") }}
              </div>
              <div v-if="task.workspaces_shared_in?.length" class="text-xs text-common-grey">
                {{ $t("Shared in") }} {{ task.workspaces_shared_in?.length }} {{ $t("workspace") }}
              </div>
            </div>

            <div
              v-if="!inPlaybook"
              class="flex items-center justify-center w-8 h-8 ml-2 bg-white border rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
              @click="handleCloseTask">
              <X color="#FF0000" class="w-6 h-6 transition ease-in-out duration-side-menu-transition-duration" /><span
                class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                {{ $t("Close") }}</span>
            </div>
          </div>
          <div :class="{ flex: !inPlaybook }">
            <div :class="{ 'w-full': inPlaybook }" class="w-2/3 -space-y-px rounded-md shadow-sm">
              <TextInput
                :disabled="editing_disabled"
                autocomplete="on"
                :autocomplete-options="resultReferencesListForText"
                :label="$t('Task name')"
                name="name"
                type="text"
                :value="task.name"
                :auto-focus="true" />
            </div>
            <div class="w-1/2 h-0 ml-6 -mt-6" :class="{ 'h-24 mt-2 w-[800px] ml-20': inPlaybook }">
              <CompaniesSelect
                :short-king="inPlaybook"
                :open-position="inPlaybook ? 'right' : 'center'"
                :multi-select="inPlaybook ? true : false"
                :autocomplete-options="resultReferencesList"
                :disabled="isTemplate || editing_disabled"
                :label="$t('Company/Opportunity')"
                :companies="selected_company ? [selected_company] : selected_companies ? selected_companies : []"
                @changed="
                  ($event) => {
                    handleSetCompaniesList($event)
                  }
                " />
            </div>
          </div>
          <div class="flex w-full">
            <div class="w-1/2 mt-3 mb-5">
              <TeamMembersSelect
                v-model:selected-team-members="selected_users"
                :autocomplete-options="autocompleteOptionsInPlaybook"
                :disabled="editing_disabled || isTemplate"
                :label="$t('Team Members')" />
            </div>
            <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
              <ContactsSelect
                v-model:selected-contacts="selected_contacts"
                :disabled="editing_disabled || isTemplate"
                :label="$t('Contacts')"
                :companies="[selected_company]" />
            </div>
          </div>

          <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
            <TextEditor
              ref="quill"
              v-model:content="task_description"
              :value="task_description"
              name="description"
              :label="$t('Description')"
              content-type="html"
              input-classes="rounded-md"
              :mention-items="resultReferencesListForText"
              theme="bubble"
              toolbar="essential" />
          </div>

          <div class="flex flex-row justify-between gap-2">
            <div class="relative w-full">
              <label
                for="start_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  input_start_date_is_focused || !!start_date ? 'top-0' : 'top-1/2',
                  is_box_back_ground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  input_start_date_is_focused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Start date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="start_date"
                  v-model="start_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'click' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="start_date"
                        :value="inputValue"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !is_box_back_ground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusStartDateInput"
                        @focusout="focusStartDateInput"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            start_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                  </template>
                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                        {{ $t("Clear") }}
                      </button>

                      <button type="button" class="cancel-btn inline-block" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="relative w-full">
              <label
                for="update_task_d_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  !!relative_date_text || input_due_date_is_focused || !!due_date ? 'top-0' : 'top-1/2',
                  is_box_back_ground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  input_due_date_is_focused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]"
                @click="is_modal_relative_date_open = true">{{ $t("Due date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="due_date"
                  v-model="due_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'none' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="update_task_d_date"
                        :value="inputValue ? inputValue : relative_date_text"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !is_box_back_ground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusDueDateInput"
                        @focusout="focusDueDateInput"
                        @click="is_modal_relative_date_open = true"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            due_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                    <div v-if="is_modal_relative_date_open" class="absolute z-20">
                      <DateTimeFrame
                        class="mr-2 w-full bg-white dark:bg-dark-box-background rounded-lg"
                        :relative-date="relative_date"
                        @done="
                          ($event) => {
                            due_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @relative-date-changed="
                          ($event) => {
                            relative_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @cancel="is_modal_relative_date_open = false" />
                    </div>
                  </template>

                  <template #footer>
                    <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                      <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                        {{ $t("Clear") }}
                      </button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>

            <SelectInput
              :disabled="editing_disabled"
              name="priority"
              :items="task_priority"
              label-attr="name"
              :allow-null="false"
              classes="w-1/2"
              value-attr="value"
              :is-box-background="true"
              :label="$t('Priority')"
              :show-label="true"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
          </div>

          <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-4 mb-4 mr-6 gap-x-2">
            <Switch
              v-model="auto_set_start_date"
              :class="[
                auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : auto_set_start_date
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
            <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
          </div>

          <div class="relative rounded-md shadow-sm border p-2 mt-2">
            <div class="flex items-center justify-between w-full">
              <label class="block text-common-grey">{{ $t("Tags") }}</label>
              <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
              </button>
            </div>
            <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
              <div
                v-for="tag in selected_tags"
                :key="tag?.id"
                class="flex focus:outline-none rounded-md"
                :style="{ background: all_tags.find((t) => t.id == tag.id)?.color }">
                <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                  <span
                    class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                    :style="{
                      background: all_tags.find((t) => t.id == tag.id)?.color
                    }">
                    <span class="block text-white truncate">{{ tag.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <AttachmentsField
            v-model="attachments"
            v-model:deleted-attachments="deleted_attachments"
            v-model:new-attachments="new_attachments"
            :disabled="editing_disabled"
            class="mt-4 mb-6" />

          <div v-if="!editing_disabled" class="flex flex-row justify-between w-full gap-2 pt-12">
            <button type="button" :class="{ 'w-1/2': inPlaybook }" class="w-1/3 cancel-btn" @click="handleCloseTask">
              {{ $t("Cancel") }}
            </button>
            <button
              v-if="!inPlaybook"
              type="button"
              class="w-1/3 delete-btn"
              @click="
                $emitter.emit(ALL_EMITTERS.DELETE_OBJECT, {
                  object: { ...task, type: 'Task' },
                  objectDeleteCallBack: () => {
                    $emit('cancel')
                  }
                })
              ">
              {{ $t("Delete") }}
            </button>
            <button :disabled="submitting_is_on" :class="{ 'w-1/2': inPlaybook }" class="w-1/3 primary-btn" @click="updateTask(values)">
              <RotateCw
                :class="{
                  'animate-spin': submitting_is_on,
                  'opacity-100': submitting_is_on
                }"
                class="w-5 h-5 mr-3 -ml-1 opacity-0"
                aria-hidden="true" />

              {{ isTemplate ? $t("Save Task as Template") : inPlaybook ? $t("Save Task") : $t("Update Task") }}
            </button>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Add tags -->
  <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note deleted')">
    <template #form="{ close: closeModal }">
      <TagsAutoCompleteInput
        :placeholder="$t('Add new tag')"
        value-attr="full_name"
        :selected-tags="selected_tags"
        @changed="
          ($event) => {
            handleTagsChanged($event)
          }
        "
        @close="closeModal" />
    </template>
  </Modal>
</template>

<script>
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import TextInput from "@/components/shared/TextInput.vue"
import dataService from "@/services/data.service"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import { Share2, Plus, RotateCw, X } from "lucide-vue-next"
import { MODULES } from "@/store/constants/references"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { useI18n } from "vue-i18n"
import TextEditor from "@/components/shared/TextEditor.vue"

import { Switch } from "@headlessui/vue"

export default {
  components: {
    Modal,
    Switch,
    Plus,
    TagsAutoCompleteInput,
    TextEditor,
    RotateCw,
    DateTimeFrame,
    TextInput,
    DatePicker,
    SelectInput,
    Form,
    ContactsSelect,
    TeamMembersSelect,
    Share2,
    X,
    ToggleStatusButton,
    CompaniesSelect,
    AttachmentsField
  },
  inject: ["canUserEditLayout", "isModuleActivated"],
  props: {
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    inPlaybook: {
      type: Boolean,
      default: false
    },

    isTemplate: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      required: true
    }
  },

  emits: ["cancel", "submitted", "error", "done"],
  setup(props) {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Task name is required")),
      description: Yup.string().nullable(),
      priority: Yup.string()
    })
    const task_clone = { ...props.task }

    delete task_clone.attachments
    delete task_clone.workspaces_shared_in
    const initial_values = task_clone

    return {
      task_clone,
      schema,
      initial_values
    }
  },

  data() {
    return {
      auto_set_start_date: false,
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      status: "pending",
      task_description: "",
      submitting_is_on: false,
      ALL_EMITTERS,
      MODULES,
      attachments: [],
      selected_companies: [],
      deleted_attachments: [],
      new_attachments: [],
      selected_users: [],
      selected_contacts: [],
      selected_company: null,
      due_date: null,
      start_date: null,
      relative_date: null,
      relative_date_text: null,
      input_due_date_is_focused: false,
      input_start_date_is_focused: false,
      is_modal_relative_date_open: false,
      task_priority: [
        { name: this.$t("Lowest"), value: "lowest" },
        { name: this.$t("Low"), value: "low" },
        { name: this.$t("Normal"), value: "normal" },
        { name: this.$t("High"), value: "high" },
        { name: this.$t("Highest"), value: "highest" }
      ],
      task_displayed: null,
      is_box_back_ground: false
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false

      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  watch: {
    status() {
      if (this.status == "in-progress" && this.auto_set_start_date == true && !this.start_date) {
        this.start_date = new Date()
      }
    },
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    }
  },

  created() {
    if (this.task?.auto_start_date_set) {
      this.auto_set_start_date = this.task?.auto_start_date_set
    }

    if (this.inPlaybook) {
      const att_calc = []
      this.task.attachments?.forEach((attachment_id) => {
        dataService.getAttachment(attachment_id).then((res) => {
          att_calc.push(res.data)
          if (att_calc.length == this.task.attachments.length) {
            this.attachments = att_calc
          }
        })
      })
    } else if (this.task.attachments) {
      this.attachments = this.task.attachments.results
    }

    if (!this.isTemplate) {
      this.selected_users = this.task.assigned_to.map((user) =>
        user.id ? user : this.$store.state.data.users.concat(this.autocompleteOptionsInPlaybook).find((item) => item.id == user)
      )
      this.selected_contacts = this.task.contacts
      this.selected_company = this.task.record
    }

    if (this.task.companies) {
      this.selected_companies = []
      this.task.companies.forEach((c) => {
        if (c.includes("$")) {
          this.selected_companies.push(c)
        } else {
          this.$store.dispatch("data/getRecord", c).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      })
      this.task.companies
    }

    this.status = this.task.status
    if (this.task.tags.length > 0) {
      this.all_tags = this.$store.state.data.actions_tags
      this.all_tags.map((tag) => {
        if (this.task.tags.find((t) => t == tag.id)) {
          this.selected_tags.push(tag)
        }
      })
    } else {
      this.selected_tags = []
    }
    this.task_displayed = this.task

    this.task_description = this.task?.description
    if (this.task.due_date) {
      this.due_date = this.$moment.utc(this.task.due_date).local().toDate()
    }
    if (this.task.start_date) {
      this.start_date = this.$moment.utc(this.task.start_date).local().toDate()
    }
    if (
      this.task.due_date_relative_to &&
      (this.inPlaybook ||
        (this.task.due_date_relative_to == "start_date" && !this.task.start_date) ||
        (this.task.due_date_relative_to == "in_progress_date" && this.task.status !== "in-progress"))
    ) {
      this.relative_date = {
        due_date_relative_to: this.task.due_date_relative_to,
        due_date_days_offset: this.task.due_date_days_offset
      }
    }
    // if in template set relative date
    if (this.isTemplate) {
      this.relative_date = {
        due_date_relative_to: this.task.due_date_relative_to,
        due_date_days_offset: this.task.due_date_days_offset
      }
      // st relative_date_text if due_date_relative_to is set
      if (this.task.due_date_relative_to) {
        if (this.task.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.task.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.task.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.task.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.task.due_date_days_offset} ` + this.$t("days from In Progress")
        }
      }
    }
  },

  methods: {
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },
    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    shareTask(task) {
      this.$emitter.emit(ALL_EMITTERS.SHARE_OBJECT, {
        object: { ...task, type: "Task" },
        objectDeleteCallBack: () => {
          this.$emit("cancel")
        },
        show_confirmation: true
      })
    },
    handleRestoreStatus() {
      if (this.task.status != this.task_clone.status) {
        const data = {}
        data["task"] = {
          id: this.task.id,
          record_full: this.task.record,
          status: this.task.status,
          dont_load: true
        }
        this.$store.dispatch("data/updateTask", data).then(() => {})
      }
    },

    handleCloseTask() {
      this.handleRestoreStatus()
      this.$emit("cancel")
    },

    handleSetCompaniesList(e) {
      if (this.inPlaybook) {
        this.selected_companies = e
      } else {
        this.selected_company = e[0] ? e[0] : null
      }
    },
    focusStartDateInput() {
      this.input_start_date_is_focused = !this.input_start_date_is_focused
    },
    focusDueDateInput() {
      this.input_due_date_is_focused = !this.input_due_date_is_focused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    updateTask(values) {
      this.submitting_is_on = true
      let data = { ...values }
      data["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x.value ? x.value : x))
      data["contacts"] = this.selected_contacts.map((x) => x.id)
      data["record"] = this.selected_company
      data["description"] = this.task_description
      data["status"] = this.status

      if (this.task.for_template) {
        data["for_template"] = true
      }

      if (data["status"] == "overdue") {
        data["status"] = "pending"
      }

      data["tags"] = this.selected_tags.map((t) => t.id)
      if (this.start_date) {
        data["start_date"] = this.$moment(this.start_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }
      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date.due_date_days_offset
        data["due_date"] = null
      } else if (this.due_date) {
        data["due_date"] = data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }

      delete data["company"]

      let params = {
        expand: "record,contacts"
      }
      if (this.isTemplate && !this.task.id) {
        if (this.task.for_template) {
          data["for_template"] = true
        }

        data["organization"] = this.$store.state.organization.id
        data["attachments"] = []

        data["auto_start_date_set"] = this.auto_set_start_date

        let template_data = {}
        template_data["name"] = values.name
        template_data["source_object_id"] = null
        template_data["source_object_type"] = "task"
        template_data["content"] = data
        dataService.createTemplate(template_data).then((res) => {
          this.submitting_is_on = false
          this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
          this.$emit("done")
        })
      } else if (this.isTemplate) {
        let data = {}
        if (this.task.for_template) {
          data["for_template"] = true
        }
        data["name"] = values.name
        data["source_object_id"] = this.task.id
        data["source_object_type"] = "task"
        dataService.createTemplate(data).then((res) => {
          this.$emitter.emit(ALL_EMITTERS.NEW_ACTIVITY_TEMPLATE_ADDED, res.data)
          this.submitting_is_on = false
          this.$emit("done")
        })
      } else if (this.inPlaybook) {
        if (this.task.for_template) {
          data["for_template"] = true
        }
        data["companies"] = this.selected_companies.map((c) => (c.id ? c.id : c))
        data["status"] = this.status

        if (this.relative_date) {
          data["due_date_relative_to"] = this.relative_date.due_date_relative_to
          data["due_date_days_offset"] = this.relative_date.due_date_days_offset
          data["due_date"] = null
        } else if (this.due_date) {
          data["due_date"] = this.$moment(this.due_date)
            .set("hour", this.$moment().hour())
            .set("minute", this.$moment().minute())
            .set("second", this.$moment().second())
            .toISOString()
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        } else {
          data["due_date"] = null
          data["due_date_relative_to"] = null
          data["due_date_days_offset"] = 0
        }

        if (this.attachments.length) {
          const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
          const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

          let attachment_calc = old_attachments.map((att) => att.id)

          if (new_attachments.length) {
            dataService.addAttachment({ attachments: new_attachments }).then((res) => {
              attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
              data["attachments"] = attachment_calc
            })
          } else {
            data["attachments"] = attachment_calc
          }
          this.$emit("done", data)
        } else {
          this.$emit("done", data)
        }
        this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_UPDATE_FORM, data)
      } else {
        if (this.task.id && !this.task.for_template) {
          this.$store
            .dispatch("data/updateTask", {
              task: { ...data, record: data.record?.id, record_full: data.record },
              params: params
            })
            .then((res) => {
              if (this.new_attachments.length) {
                let formData = new FormData()
                this.new_attachments.forEach((attachment, i) => {
                  Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                })
                this.$store.dispatch("data/addAttachmentsToTask", {
                  id: res.id,
                  attachments: formData,
                  record_full: data.record
                })
              }

              if (this.deleted_attachments.length) {
                this.$store.dispatch("data/deleteAttachmentsFromTask", {
                  id: res.id,
                  record_full: data.record,
                  attachments_ids: this.deleted_attachments.map((x) => x.id)
                })
              }

              this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, this.due_date ? new Date(this.due_date) : null)
              this.$emit("done", res)
              this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_CREATE_FORM, res)
              this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_UPDATE_FORM, res)
              this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_CREATE_FORM, res)
              this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_UPDATE_FORM, res)
              this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_AI_PROMPT, res)

              this.submitting_is_on = false
            })
            .catch(() => {
              this.submitting_is_on = false

              // To do show exceptions
              this.$emit("error")
            })
        } else {
          //
          if (this.task.for_template) {
            data["for_template"] = true
          }
          this.submitting_is_on = false

          this.$emit("done", data)
          this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_PLAN_CREATE_FORM, data)
          this.$emitter.emit(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_CREATE_FORM, data)
        }
      }
    }
  }
}
</script>
